/**
 * Shared `nb-NO` date + time formatting for admin screens (display-only —
 * never used for anything load-bearing). Falls back to the raw ISO string if
 * it can't be parsed, so a malformed value never crashes the page.
 */
export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat("nb-NO", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
}

/**
 * Norwegian relative time ("nå nettopp", "3 dager siden", …) for comment
 * timestamps (Steg F6). Display-only — never load-bearing. Falls back to the
 * raw ISO string if it can't be parsed, and clamps a future timestamp (clock
 * skew) to "nå nettopp" rather than a nonsensical negative duration.
 *
 * `now` is injectable for deterministic unit tests; it defaults to the
 * current time in normal use.
 */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 60) return "nå nettopp";

  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? "minutt" : "minutter"} siden`;

  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `${diffHour} ${diffHour === 1 ? "time" : "timer"} siden`;

  const diffDay = Math.round(diffHour / 24);
  if (diffDay < 7) return `${diffDay} ${diffDay === 1 ? "dag" : "dager"} siden`;

  const diffWeek = Math.round(diffDay / 7);
  if (diffWeek < 5) return `${diffWeek} ${diffWeek === 1 ? "uke" : "uker"} siden`;

  const diffMonth = Math.round(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth} ${diffMonth === 1 ? "måned" : "måneder"} siden`;

  const diffYear = Math.round(diffDay / 365);
  return `${diffYear} ${diffYear === 1 ? "år" : "år"} siden`;
}
